import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode } from '../../context/ThemeContext';
import { Link as RouterLink } from 'react-router-dom';
import { UserRoleMap } from '../../types';

interface HeaderProps {
  onMenuClick: () => void;
}

const Glyph = ({ name, size = '1.35rem' }: { name: string; size?: string }) => (
  <Box
    component="span"
    className="material-symbols-rounded"
    sx={{
      fontSize: size,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {name}
  </Box>
);

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const userInitial = useMemo(() => {
    const display = user?.displayName?.trim();
    if (display && display.length > 0) {
      return display.charAt(0).toUpperCase();
    }
    const email = (user as { email?: string } | undefined)?.email;
    return email ? email.charAt(0).toUpperCase() : 'U';
  }, [user]);

  const getRoleIcon = (role?: string) => {
    switch (role) {
      case 'admin':
        return 'workspace_premium';
      case 'customer':
        return 'store';
      case 'partner':
        return 'handshake';
      default:
        return 'person';
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setAnchorEl(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
        borderBottom: theme.palette.mode === 'dark' 
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(0, 0, 0, 0.08)',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: { xs: 1, md: 2 } }}>
        {isMobile && (
          <IconButton
            onClick={onMenuClick}
            sx={{
              borderRadius: 3,
              bgcolor: theme.palette.mode === 'dark' 
                ? 'rgba(9, 15, 28, 0.28)'
                : 'rgba(255, 255, 255, 0.6)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.06)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 20px rgba(8, 14, 26, 0.4)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
              color: theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(9, 15, 28, 0.4)'
                  : 'rgba(255, 255, 255, 0.8)',
              },
            }}
          >
            <Glyph name="menu" />
          </IconButton>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            component={RouterLink}
            to="/dashboard"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              borderRadius: 2,
              px: 0.25,
              py: 0.25,
              transition: 'transform 160ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
              },
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.light}`,
                outlineOffset: 4,
              },
            }}
          >
            <Box
              sx={{
                width: { xs: 44, md: 52 },
                height: { xs: 44, md: 52 },
                borderRadius: 16,
                display: 'grid',
                placeItems: 'center',
                backgroundImage: theme.palette.mode === 'dark'
                  ? 'linear-gradient(140deg, rgba(31, 60, 136, 0.65) 0%, rgba(61, 101, 245, 0.55) 100%)'
                  : 'linear-gradient(140deg, rgba(61, 101, 245, 0.15) 0%, rgba(107, 138, 255, 0.12) 100%)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.18)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 12px 32px rgba(15, 24, 46, 0.48)'
                  : '0 4px 16px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="EGMMS 로고"
                sx={{
                  width: '70%',
                  height: '70%',
                  objectFit: 'contain',
                  filter: theme.palette.mode === 'dark'
                    ? 'drop-shadow(0 4px 12px rgba(8, 14, 26, 0.35))'
                    : 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.015em',
                  fontSize: { xs: '1rem', md: '1.25rem' },
                }}
              >
                EGMMS
              </Typography>
              <Typography
                variant="body2"
                sx={{ 
                  color: theme.palette.mode === 'dark' 
                    ? 'rgba(245, 247, 255, 0.64)' 
                    : 'rgba(65, 90, 119, 0.7)', 
                  fontSize: { xs: '0.72rem', md: '0.82rem' } 
                }}
              >
                Smart Mold Management Suite
              </Typography>
            </Box>
          </Box>
          {!isMobile && (
            <Chip
              icon={<Glyph name="bolt" size="1.1rem" />}
              label="실시간 동기화"
              color="secondary"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                px: 0.5,
              }}
            />
          )}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" spacing={1.5} alignItems="center">
          {user && (
            <Stack direction="row" spacing={1} alignItems="center">
              {!isMobile && user.role && (
                <Chip
                  icon={<Glyph name={getRoleIcon(user.role)} size="1.15rem" />}
                  label={UserRoleMap[user.role as keyof typeof UserRoleMap] || '일반 사용자'}
                  sx={{
                    backgroundImage: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(192, 132, 87, 0.32), rgba(192, 132, 87, 0.12))'
                      : 'linear-gradient(135deg, rgba(192, 132, 87, 0.2), rgba(192, 132, 87, 0.08))',
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.12)'
                      : '1px solid rgba(0, 0, 0, 0.12)',
                    color: '#1a1a1a',
                    '& .material-symbols-rounded': {
                      color: '#1a1a1a',
                    },
                  }}
                />
              )}

              <Tooltip title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}>
                <IconButton
                  onClick={toggleTheme}
                  sx={{
                    borderRadius: 3,
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(9, 15, 28, 0.28)'
                      : 'rgba(255, 255, 255, 0.6)',
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.06)'
                      : '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 20px rgba(8, 14, 26, 0.4)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    color: theme.palette.text.primary,
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(9, 15, 28, 0.4)'
                        : 'rgba(255, 255, 255, 0.8)',
                    },
                  }}
                >
                  <Glyph name={mode === 'dark' ? 'light_mode' : 'dark_mode'} size="1.25rem" />
                </IconButton>
              </Tooltip>

              <Tooltip title={user.displayName ?? '프로필'}>
                <IconButton
                  onClick={handleMenuOpen}
                  sx={{
                    p: 0,
                    borderRadius: 3,
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.12)'
                      : '1px solid rgba(0, 0, 0, 0.12)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 0 0 2px rgba(61, 101, 245, 0.15)'
                      : '0 0 0 2px rgba(61, 101, 245, 0.08)',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontWeight: 600,
                      backgroundImage: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(61, 101, 245, 0.6), rgba(31, 60, 136, 0.9))'
                        : 'linear-gradient(135deg, rgba(61, 101, 245, 0.8), rgba(107, 138, 255, 0.9))',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.18)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                      color: '#ffffff',
                    }}
                  >
                    {userInitial}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
          },
        }}
      >
        <MenuItem disabled sx={{ opacity: 0.8 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">{user?.displayName}</Typography>
            <Typography variant="caption" sx={{ letterSpacing: '0.06em' }}>
              {user?.role ? UserRoleMap[user.role as keyof typeof UserRoleMap] : '일반 사용자'}
            </Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Glyph name="logout" size="1.2rem" />
            <Typography variant="body2">로그아웃</Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default Header;
